import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useLocation } from 'react-router-dom'
import { House, Plus, Trash2, User as UserIcon } from 'lucide-react'
import { MY_TASKS_KEY } from '@witty-board/shared'
import { usePrefetchProjectTasks, useProjectsQuery } from '../api/projects.js'
import { useLogoutMutation, useMeQuery } from '../api/auth.js'
import { CreateProjectModal } from '../features/project/CreateProjectModal.js'
import { Avatar } from './Avatar.js'
import { ProjectSwatch } from './ProjectSwatch.js'

/**
 * Elemento activo: fondo `--surface-hi` y barra de 3px en `--accent` a la
 * izquierda (CLAUDE.md §6). Es uno de los tres usos permitidos del acento.
 */
const navItemClasses =
  'flex h-10 max-md:h-11 shrink-0 items-center gap-3 rounded-card px-3 text-body text-text transition-colors duration-100 hover:bg-surface-hi focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
const activeNavItemClasses =
  'relative bg-surface-hi before:absolute before:inset-y-1 before:left-0 before:w-[3px] before:rounded-full before:bg-accent'

interface SidebarProps {
  /** Cajón abierto en móvil (CLAUDE.md §6, fase 6); ignorado en escritorio, donde la barra siempre está visible. */
  open: boolean
  onClose: () => void
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const { t } = useTranslation()
  const me = useMeQuery()
  const logout = useLogoutMutation()
  const projects = useProjectsQuery()
  const prefetchProjectTasks = usePrefetchProjectTasks()
  const [menuOpen, setMenuOpen] = useState(false)
  const [creatingProject, setCreatingProject] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  // Cálculo manual en vez de `NavLink`'s `isActive`: con la tarea abierta la
  // URL real es `/projects/TASKS/tasks/:id` o `/projects/:key/tasks/:id`, y
  // un único `NavLink` no puede reflejar dos prefijos de ruta a la vez.
  const homeActive =
    location.pathname === '/' || location.pathname.startsWith(`/projects/${MY_TASKS_KEY}`)

  useEffect(() => {
    if (!menuOpen) return

    function onPointerDown(event: PointerEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  return (
    <>
      {/* Fondo del cajón en móvil: cierra al tocar fuera. No existe en escritorio (md:hidden). */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          aria-hidden="true"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex h-dvh w-72 shrink-0 flex-col bg-bg px-3 py-4 transition-transform duration-200 md:static md:z-auto md:w-60 md:translate-x-0 ${open ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex h-10 shrink-0 items-center px-3">
          <img src="/logo.png" alt="Witty Games" className="h-8 w-auto" />
        </div>

        <nav className="mt-4 flex shrink-0 flex-col gap-1">
          <NavLink
            to="/"
            onClick={onClose}
            className={`${navItemClasses} ${homeActive ? activeNavItemClasses : ''}`}
          >
            <House className="h-4 w-4 shrink-0 text-text-muted" aria-hidden="true" />
            {t('sidebar.home')}
          </NavLink>
        </nav>

        <div className="my-3 h-px shrink-0 bg-border" />

        <div className="flex shrink-0 items-center justify-between px-3 pb-1">
          <span className="text-meta text-text-muted">{t('sidebar.projects')}</span>
          <button
            type="button"
            onClick={() => setCreatingProject(true)}
            aria-label={t('project.create.title')}
            className="flex h-6 w-6 max-md:h-11 max-md:w-11 items-center justify-center rounded-card text-text-muted hover:bg-surface-hi hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto">
          {projects.isPending &&
            [0, 1, 2].map((index) => (
              <div
                key={index}
                aria-hidden="true"
                className="h-10 shrink-0 animate-pulse rounded-card bg-surface"
              />
            ))}
          {projects.data?.map((project) => {
            const isActive =
              location.pathname === `/projects/${project.key}` ||
              location.pathname.startsWith(`/projects/${project.key}/`)
            return (
              <NavLink
                key={project.id}
                to={`/projects/${project.key}`}
                onMouseEnter={() => prefetchProjectTasks(project.id)}
                onClick={onClose}
                className={`${navItemClasses} ${isActive ? activeNavItemClasses : ''}`}
              >
                <ProjectSwatch color={project.color} />
                <span className="truncate">{project.name}</span>
              </NavLink>
            )
          })}
        </nav>

        <div ref={menuRef} className="relative mt-3 shrink-0">
          {menuOpen && (
            <div className="absolute bottom-full left-0 mb-2 w-full rounded-panel bg-surface p-1 shadow">
              <NavLink
                to="/profile"
                onClick={() => {
                  setMenuOpen(false)
                  onClose()
                }}
                className="flex h-10 max-md:h-11 w-full items-center gap-2 rounded-card px-3 text-left text-body text-text hover:bg-surface-hi focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                <UserIcon className="h-4 w-4 shrink-0 text-text-muted" aria-hidden="true" />
                {t('sidebar.profile')}
              </NavLink>
              <NavLink
                to="/trash"
                onClick={() => {
                  setMenuOpen(false)
                  onClose()
                }}
                className="flex h-10 max-md:h-11 w-full items-center gap-2 rounded-card px-3 text-left text-body text-text hover:bg-surface-hi focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                <Trash2 className="h-4 w-4 shrink-0 text-text-muted" aria-hidden="true" />
                {t('trash.title')}
              </NavLink>
              <button
                type="button"
                onClick={() => logout.mutate()}
                disabled={logout.isPending}
                className="flex h-10 max-md:h-11 w-full items-center rounded-card px-3 text-left text-body text-text hover:bg-surface-hi focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
              >
                {t('auth.logout')}
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={() => setMenuOpen((value) => !value)}
            aria-expanded={menuOpen}
            aria-label={t('sidebar.profileMenu')}
            className="flex h-10 max-md:h-11 w-full items-center gap-2 rounded-card px-3 text-body text-text hover:bg-surface-hi focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            {me.data && <Avatar name={me.data.name} avatarUrl={me.data.avatarUrl} size="md" />}
            <span className="truncate">{me.data?.name}</span>
          </button>
        </div>

        {creatingProject && <CreateProjectModal onClose={() => setCreatingProject(false)} />}
      </aside>
    </>
  )
}
